import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowUpRight,
    ArrowRight,
    Check,
    Copy,
    Building2,
    ShieldCheck,
    Zap,
    FileText,
    Cpu,
    TrendingUp,
    AlertTriangle,
    Clock,
    ChevronDown,
    Layers,
    Eye,
    Code2,
    Globe2,
    Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/* ─────────────────────── Hero ─────────────────────── */

function BsaHero() {
    return (
        <section className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-hero pointer-events-none" />
            <div className="absolute inset-0 bg-grid-subtle bg-grid-fade opacity-60 pointer-events-none" />
            <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-accent/10 blur-[140px] pointer-events-none" />

            <div className="relative container max-w-[1240px] mx-auto px-6 pt-16 md:pt-24 pb-16 md:pb-24">
                <Link to="/" className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground hover:text-foreground transition-colors">
                    ← Back to CredNX
                </Link>

                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 mt-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/20 border border-accent/30 text-foreground">
                            <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse-soft" />
                            <span className="text-[11.5px] tracking-[0.18em] uppercase font-semibold">Live Product</span>
                        </div>
                        <h1
                            data-testid="bsa-hero-title"
                            className="mt-6 font-serif text-[44px] sm:text-[60px] lg:text-[74px] leading-[0.98] tracking-[-0.035em] text-balance"
                        >
                            Bank Statement{" "}
                            <span className="italic">Analysis</span>
                            <br />
                            for every lender, every bank.
                        </h1>
                        <p className="mt-6 text-[17px] sm:text-[18.5px] leading-[1.6] text-muted-foreground max-w-[620px] text-pretty">
                            Drop in a PDF, CSV or scanned statement — get back a uniform schema
                            with transaction enrichment, cash-flow metrics, and fraud flags. No
                            bank-specific parsers to maintain, ever.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Button variant="hero" size="xl" asChild>
                                <Link to="/contact">
                                    Book a private demo <ArrowUpRight className="w-4 h-4 ml-1" />
                                </Link>
                            </Button>
                            <Button variant="outline-dark" size="xl" asChild>
                                <a href="#api-preview">
                                    See the API <ArrowRight className="w-4 h-4 ml-1" />
                                </a>
                            </Button>
                        </div>
                    </div>

                    <div className="lg:col-span-4 grid grid-cols-2 gap-3">
                        {[
                            { k: "220+", l: "banks supported" },
                            { k: "41s", l: "avg parse time" },
                            { k: "99.3%", l: "field accuracy" },
                            { k: "5", l: "live markets" },
                        ].map((s) => (
                            <div
                                key={s.l}
                                className="rounded-xl bg-card border border-border p-5"
                            >
                                <p className="font-serif text-[34px] leading-none tracking-[-0.03em]">{s.k}</p>
                                <p className="mt-2 text-[12px] tracking-wider uppercase text-muted-foreground">{s.l}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────── Live banks ─────────────────────── */

const BANKS_BY_MARKET = {
    Indonesia: ["Bank Mandiri", "BCA", "BNI", "BRI", "CIMB Niaga", "Permata", "Danamon", "OCBC NISP", "Panin", "Bank Jago", "BTPN", "BSI"],
    Singapore: ["DBS", "OCBC", "UOB", "Standard Chartered", "HSBC", "Maybank SG", "Citibank"],
    Malaysia: ["Maybank", "CIMB", "Public Bank", "RHB", "Hong Leong", "AmBank", "Alliance", "Bank Islam"],
    Philippines: ["BDO", "Metrobank", "BPI", "Landbank", "UnionBank", "Security Bank", "PNB"],
    Vietnam: ["Vietcombank", "Techcombank", "VPBank", "BIDV", "VietinBank", "Sacombank"],
};

function LiveBanks() {
    const [active, setActive] = useState("Indonesia");
    return (
        <section className="relative">
            <div className="container max-w-[1240px] mx-auto px-6 py-24 md:py-28">
                <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
                    <div className="lg:col-span-7">
                        <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">Coverage</p>
                        <h2 className="mt-4 font-serif text-[38px] md:text-[52px] leading-[1.02] tracking-tight text-balance">
                            <span className="italic">Live</span> — right now.
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-[15.5px] text-muted-foreground max-w-[480px] lg:ml-auto">
                        We don&apos;t ship bank logos we haven&apos;t processed. Every name below has at least 10,000 statements in production.
                    </p>
                </div>

                <div className="flex flex-wrap gap-2 mb-8" role="tablist">
                    {Object.keys(BANKS_BY_MARKET).map((m) => (
                        <button
                            key={m}
                            onClick={() => setActive(m)}
                            role="tab"
                            aria-selected={active === m}
                            data-testid={`bank-tab-${m.toLowerCase()}`}
                            className={cn(
                                "px-4 py-2 rounded-full text-[13px] font-medium transition-all border",
                                active === m
                                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                    : "bg-card text-foreground/75 border-border hover:border-primary/30 hover:text-foreground"
                            )}
                        >
                            {m}
                            <span className="ml-2 text-[11px] opacity-60 font-mono">
                                {BANKS_BY_MARKET[m].length}
                            </span>
                        </button>
                    ))}
                </div>

                <motion.div
                    key={active}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3"
                >
                    {BANKS_BY_MARKET[active].map((b) => (
                        <div
                            key={b}
                            className="relative rounded-xl border border-border bg-card px-4 py-5 hover:border-primary/30 hover:-translate-y-0.5 transition-all"
                        >
                            <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-success" />
                                <span>Live</span>
                            </div>
                            <p className="font-serif text-[17px] tracking-tight leading-tight">{b}</p>
                        </div>
                    ))}
                </motion.div>

                <p className="mt-8 text-[13px] text-muted-foreground">
                    Don&apos;t see your bank?{" "}
                    <Link to="/contact" className="text-primary font-medium link-underline">
                        We onboard new banks in under 14 days.
                    </Link>
                </p>
            </div>
        </section>
    );
}

/* ─────────────────────── Sample report walkthrough ─────────────────────── */

const REPORT_TABS = [
    { key: "summary", label: "Summary", icon: Eye },
    { key: "transactions", label: "Transactions", icon: FileText },
    { key: "flows", label: "Cash flow", icon: TrendingUp },
    { key: "flags", label: "Risk flags", icon: AlertTriangle },
];

function SampleReport() {
    const [tab, setTab] = useState("summary");
    return (
        <section className="relative bg-background-soft/70 border-y border-border">
            <div className="absolute inset-0 bg-gradient-mesh-soft opacity-40 pointer-events-none" />
            <div className="relative container max-w-[1240px] mx-auto px-6 py-24 md:py-32">
                <div className="max-w-[740px] mb-12">
                    <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">Sample report</p>
                    <h2 className="mt-4 font-serif text-[38px] md:text-[54px] leading-[1.02] tracking-tight text-balance">
                        A walkthrough of one real{" "}
                        <span className="italic">output</span>.
                    </h2>
                    <p className="mt-5 text-[15.5px] leading-relaxed text-muted-foreground max-w-[560px]">
                        This is a redacted 6-month Bank Mandiri statement for a small manufacturer — everything below came from a single API call.
                    </p>
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap gap-2 mb-6" role="tablist">
                    {REPORT_TABS.map((t) => (
                        <button
                            key={t.key}
                            role="tab"
                            aria-selected={tab === t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`report-tab-${t.key}`}
                            className={cn(
                                "inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[13px] font-medium border transition-all",
                                tab === t.key
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-card text-foreground/75 border-border hover:border-primary/30 hover:text-foreground"
                            )}
                        >
                            <t.icon className="w-3.5 h-3.5" /> {t.label}
                        </button>
                    ))}
                </div>

                <motion.div
                    key={tab}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    className="rounded-2xl border border-border bg-card shadow-elegant overflow-hidden"
                >
                    {tab === "summary" && <ReportSummary />}
                    {tab === "transactions" && <ReportTransactions />}
                    {tab === "flows" && <ReportFlows />}
                    {tab === "flags" && <ReportFlags />}
                </motion.div>
            </div>
        </section>
    );
}

function ReportSummary() {
    return (
        <div className="grid md:grid-cols-12 divide-y md:divide-y-0 md:divide-x divide-border">
            <div className="md:col-span-4 p-8 bg-background-soft/60">
                <p className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground">Borrower</p>
                <p className="mt-2 font-serif text-[26px] tracking-tight leading-tight">PT Andalan Manufaktur</p>
                <p className="mt-1 text-[13px] text-muted-foreground font-mono">crednx.bsa_v4 · mandiri_idn</p>
                <dl className="mt-6 space-y-3 text-[14px]">
                    {[
                        ["Statement period", "01 Aug 2025 — 31 Jan 2026"],
                        ["Opening balance", "IDR 128,400,000"],
                        ["Closing balance", "IDR 382,910,000"],
                        ["Statement parse", "OK · 99.8%"],
                    ].map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-4">
                            <dt className="text-muted-foreground">{k}</dt>
                            <dd className="text-foreground font-medium text-right">{v}</dd>
                        </div>
                    ))}
                </dl>
            </div>
            <div className="md:col-span-8 p-8">
                <div className="flex items-center justify-between">
                    <p className="font-serif text-[22px] tracking-tight">Underwriting signal</p>
                    <span className="text-[11.5px] tracking-[0.16em] uppercase text-success font-semibold">Approved tier · A−</span>
                </div>
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                        { k: "Net surplus", v: "IDR 121M", d: "monthly avg." },
                        { k: "Surplus ratio", v: "25.1%", d: "stable 6mo." },
                        { k: "Inflow variance", v: "0.18", d: "low volatility" },
                        { k: "Counterparties", v: "38", d: "well diversified" },
                    ].map((m) => (
                        <div key={m.k} className="rounded-xl border border-border bg-background-soft/40 p-4">
                            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{m.k}</p>
                            <p className="mt-2 font-serif text-[24px] leading-none tracking-[-0.02em]">{m.v}</p>
                            <p className="mt-1 text-[12px] text-muted-foreground">{m.d}</p>
                        </div>
                    ))}
                </div>

                <div className="mt-6 rounded-xl bg-primary text-primary-foreground p-5 flex items-start gap-4">
                    <Sparkles className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                    <div>
                        <p className="text-[13px] font-medium">CredNX summary</p>
                        <p className="mt-1.5 text-[13.5px] leading-relaxed text-primary-foreground/75">
                            Revenue shows healthy seasonality (Q4 uplift). No circular transfers detected. One recurring supplier dependency (38% of outflow) — flagged for manual review, not rejection.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ReportTransactions() {
    const rows = [
        { d: "14 Feb", desc: "PT Sinar Utama · invoice INV-883", c: "Revenue", sub: "B2B invoice", a: "+ 42,300,000", pos: true },
        { d: "13 Feb", desc: "Payroll transfer — 18 staff", c: "Payroll", sub: "Recurring", a: "− 88,450,000" },
        { d: "12 Feb", desc: "PLN · electricity", c: "Utilities", sub: "Utility bill", a: "− 3,120,000" },
        { d: "11 Feb", desc: "PT Mitra Sejahtera · inflow", c: "Revenue", sub: "B2B invoice", a: "+ 67,000,000", pos: true },
        { d: "10 Feb", desc: "Tokopedia · marketing", c: "Marketing", sub: "Ad platform", a: "− 4,800,000" },
        { d: "09 Feb", desc: "Gojek · fleet fuel reimb.", c: "Logistics", sub: "Fuel", a: "− 2,950,000" },
        { d: "08 Feb", desc: "Bank fees · service charge", c: "Fees", sub: "Bank", a: "− 32,500" },
    ];
    return (
        <div>
            <div className="grid grid-cols-12 text-[11px] uppercase tracking-wider text-muted-foreground px-6 py-3 bg-muted/50 border-b border-border">
                <div className="col-span-2">Date</div>
                <div className="col-span-5">Description</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-3 text-right">Amount</div>
            </div>
            {rows.map((r, i) => (
                <div
                    key={i}
                    className={cn(
                        "grid grid-cols-12 px-6 py-4 text-[13.5px]",
                        i !== rows.length - 1 && "border-b border-border/70"
                    )}
                >
                    <div className="col-span-2 font-mono text-muted-foreground">{r.d}</div>
                    <div className="col-span-5 text-foreground">
                        {r.desc}
                        <div className="text-[11.5px] text-muted-foreground mt-0.5">{r.sub}</div>
                    </div>
                    <div className="col-span-2">
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/5 text-primary/90 border border-primary/10">
                            {r.c}
                        </span>
                    </div>
                    <div className={cn("col-span-3 text-right font-mono", r.pos ? "text-success" : "text-foreground/85")}>
                        IDR {r.a}
                    </div>
                </div>
            ))}
        </div>
    );
}

function ReportFlows() {
    const months = ["Aug", "Sep", "Oct", "Nov", "Dec", "Jan"];
    const inflow = [402, 418, 445, 462, 505, 482];
    const outflow = [312, 324, 339, 358, 381, 361];
    const max = 520;

    return (
        <div className="p-8">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                    <p className="font-serif text-[24px] tracking-tight">Monthly cash flow</p>
                    <p className="text-[13px] text-muted-foreground mt-0.5">All figures in IDR million</p>
                </div>
                <div className="flex items-center gap-4 text-[12px]">
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <span className="w-2.5 h-2.5 rounded-sm bg-primary" /> Inflow
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        <span className="w-2.5 h-2.5 rounded-sm bg-accent" /> Outflow
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-6 gap-6 items-end h-[240px]">
                {months.map((m, i) => (
                    <div key={m} className="relative h-full flex items-end justify-center gap-2">
                        <motion.div
                            initial={{ height: 0 }}
                            whileInView={{ height: `${(inflow[i] / max) * 100}%` }}
                            viewport={{ once: true, amount: 0.5 }}
                            transition={{ duration: 0.9, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                            className="w-7 md:w-8 bg-primary rounded-t-md relative group"
                        >
                            <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] font-mono text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">{inflow[i]}</span>
                        </motion.div>
                        <motion.div
                            initial={{ height: 0 }}
                            whileInView={{ height: `${(outflow[i] / max) * 100}%` }}
                            viewport={{ once: true, amount: 0.5 }}
                            transition={{ duration: 0.9, delay: 0.12 + i * 0.05, ease: [0.16, 1, 0.3, 1] }}
                            className="w-7 md:w-8 bg-accent rounded-t-md relative group"
                        >
                            <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] font-mono text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">{outflow[i]}</span>
                        </motion.div>
                        <span className="absolute -bottom-6 text-[11.5px] text-muted-foreground font-mono">{m}</span>
                    </div>
                ))}
            </div>

            <div className="mt-12 grid md:grid-cols-3 gap-4">
                {[
                    ["Avg surplus", "IDR 121M"],
                    ["Surplus ratio", "25.1%"],
                    ["Volatility index", "0.18"],
                ].map(([k, v]) => (
                    <div key={k} className="flex items-baseline justify-between border-t border-border pt-4">
                        <p className="text-[12px] uppercase tracking-wider text-muted-foreground">{k}</p>
                        <p className="font-serif text-[22px] tracking-tight">{v}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ReportFlags() {
    const flags = [
        {
            level: "info",
            title: "Supplier concentration",
            body: "38% of outflow concentrated with PT Solusi Pasti. Recurring invoices matched — not fraudulent, but worth noting for risk policy.",
        },
        {
            level: "warn",
            title: "Round-number transfers",
            body: "3 round-number transfers of IDR 50M to individual accounts on end-of-month. Typical for director drawings; confirm purpose.",
        },
        {
            level: "ok",
            title: "No circular transfers",
            body: "No A→B→A cycles detected in the window. Counterparty graph is clean.",
        },
        {
            level: "ok",
            title: "No statement tampering",
            body: "PDF signature, font metrics and balance continuity all pass. Source bank signature validates.",
        },
    ];

    const colorMap = {
        info: "bg-primary/5 border-primary/20 text-primary",
        warn: "bg-warning/10 border-warning/30 text-warning",
        ok: "bg-success/10 border-success/30 text-success",
    };

    return (
        <div className="p-8">
            <div className="space-y-3">
                {flags.map((f, i) => (
                    <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.5, delay: i * 0.05 }}
                        className={cn("rounded-xl border p-5 flex gap-4", colorMap[f.level])}
                    >
                        <div className="shrink-0 w-9 h-9 rounded-lg bg-background/60 border border-current/30 inline-flex items-center justify-center">
                            {f.level === "warn" ? (
                                <AlertTriangle className="w-4 h-4" />
                            ) : f.level === "ok" ? (
                                <Check className="w-4 h-4" />
                            ) : (
                                <Sparkles className="w-4 h-4" />
                            )}
                        </div>
                        <div>
                            <div className="flex items-center gap-3">
                                <p className="text-[14.5px] font-medium text-foreground">{f.title}</p>
                                <span className="text-[10.5px] uppercase tracking-widest font-semibold opacity-80">
                                    {f.level}
                                </span>
                            </div>
                            <p className="mt-1.5 text-[13.5px] text-muted-foreground leading-relaxed">{f.body}</p>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    );
}

/* ─────────────────────── Capabilities ─────────────────────── */

const CAPS = [
    { icon: FileText, title: "Any format in", blurb: "PDF, image, scanned, password-locked, multi-statement, CSV, Excel." },
    { icon: Cpu, title: "Transaction enrichment", blurb: "Category, counterparty, recurring pattern — all resolved server-side." },
    { icon: TrendingUp, title: "Cash-flow metrics", blurb: "Net surplus, volatility, seasonality, DSCR, debt burden — out of the box." },
    { icon: ShieldCheck, title: "Fraud heuristics", blurb: "Tamper detection, balance continuity, round-number flags, shell transfers." },
    { icon: Layers, title: "Clean schema out", blurb: "Every bank, every market — one response object. Versioned, typed, stable." },
    { icon: Zap, title: "Fast by default", blurb: "41s average. p95 under 72s. Streaming responses for realtime UIs." },
];

function Capabilities() {
    return (
        <section className="relative">
            <div className="container max-w-[1240px] mx-auto px-6 py-24 md:py-32">
                <div className="max-w-[760px] mb-16">
                    <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">Capabilities</p>
                    <h2 className="mt-4 font-serif text-[38px] md:text-[54px] leading-[1.02] tracking-tight text-balance">
                        Everything a risk team{" "}
                        <span className="italic">actually needs.</span>
                    </h2>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                    {CAPS.map((c, i) => (
                        <motion.div
                            key={c.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.7, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                            className="group relative rounded-2xl border border-border bg-card p-7 hover:border-primary/30 hover:-translate-y-0.5 transition-all"
                        >
                            <div className="w-10 h-10 rounded-xl bg-primary/5 border border-primary/10 inline-flex items-center justify-center text-primary mb-5">
                                <c.icon className="w-4.5 h-4.5" />
                            </div>
                            <h3 className="font-serif text-[22px] tracking-tight">{c.title}</h3>
                            <p className="mt-2 text-[14px] text-muted-foreground leading-relaxed">{c.blurb}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────── API preview ─────────────────────── */

const CODE_SNIPPETS = {
    curl: `curl -X POST https://api.crednx.com/v4/bsa/sessions \\
  -H "Authorization: Bearer $CREDNX_KEY" \\
  -H "Content-Type: multipart/form-data" \\
  -F "bank=mandiri_idn" \\
  -F "statement=@./andalan_6mo.pdf"`,
    node: `import CredNX from "@crednx/sdk";

const client = new CredNX(process.env.CREDNX_KEY);

const session = await client.bsa.sessions.create({
  bank: "mandiri_idn",
  statement: fs.createReadStream("./andalan_6mo.pdf"),
});

console.log(session.summary.riskTier);   // "A-"
console.log(session.cashFlow.monthly);   // [ ... 6 months ]`,
    python: `from crednx import CredNX

client = CredNX(api_key=os.environ["CREDNX_KEY"])

session = client.bsa.sessions.create(
    bank="mandiri_idn",
    statement=open("andalan_6mo.pdf", "rb"),
)

print(session.summary.risk_tier)   # "A-"
print(session.cash_flow.monthly)   # [ ... 6 months ]`,
};

function ApiPreview() {
    const [lang, setLang] = useState("curl");
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard?.writeText(CODE_SNIPPETS[lang]);
        } catch {
            /* clipboard unavailable in sandboxed iframes — fail silently */
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
    };

    return (
        <section id="api-preview" className="relative bg-surface-deep text-surface-deep-fg overflow-hidden">
            <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
            <div className="absolute -top-20 right-0 w-[500px] h-[500px] rounded-full bg-accent/10 blur-3xl pointer-events-none" />

            <div className="relative container max-w-[1240px] mx-auto px-6 py-24 md:py-32">
                <div className="grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5">
                        <p className="text-[12px] tracking-[0.22em] uppercase text-accent/90">API</p>
                        <h2 className="mt-4 font-serif text-[38px] md:text-[54px] leading-[1.02] tracking-tight text-balance">
                            Two endpoints. <span className="italic text-surface-deep-fg/75">That&apos;s it.</span>
                        </h2>
                        <p className="mt-5 text-[15.5px] text-surface-deep-fg/65 leading-relaxed max-w-[440px]">
                            Create a session with any bank statement. Poll or stream the response. SDKs in TypeScript, Python, Go and Kotlin.
                        </p>

                        <div className="mt-8 space-y-3">
                            {[
                                { m: "POST", e: "/v4/bsa/sessions", d: "Create parse session" },
                                { m: "GET", e: "/v4/bsa/sessions/{id}", d: "Retrieve result" },
                            ].map((r) => (
                                <div key={r.e} className="rounded-xl border border-surface-deep-fg/10 bg-surface-deep-fg/[0.04] p-4 font-mono text-[13px]">
                                    <div className="flex items-center gap-3">
                                        <span className="text-[10.5px] tracking-[0.16em] uppercase font-semibold text-accent">{r.m}</span>
                                        <span className="text-surface-deep-fg">{r.e}</span>
                                    </div>
                                    <p className="mt-1.5 font-sans text-[12.5px] text-surface-deep-fg/60">{r.d}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-surface-deep-fg/10 bg-primary-deep/60 backdrop-blur overflow-hidden shadow-elegant">
                            <div className="flex items-center justify-between border-b border-surface-deep-fg/10 px-4 py-2.5">
                                <div className="flex items-center gap-1">
                                    {Object.keys(CODE_SNIPPETS).map((k) => (
                                        <button
                                            key={k}
                                            onClick={() => setLang(k)}
                                            data-testid={`api-lang-${k}`}
                                            className={cn(
                                                "px-3 py-1.5 text-[12px] font-mono rounded-md transition-colors",
                                                lang === k
                                                    ? "bg-surface-deep-fg/10 text-surface-deep-fg"
                                                    : "text-surface-deep-fg/55 hover:text-surface-deep-fg"
                                            )}
                                        >
                                            {k}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    onClick={copy}
                                    className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-surface-deep-fg/70 hover:text-accent transition-colors"
                                    aria-label="Copy code"
                                    data-testid="api-copy-button"
                                >
                                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                                    {copied ? "Copied" : "Copy"}
                                </button>
                            </div>
                            <pre className="p-6 overflow-x-auto text-[13px] leading-[1.7] font-mono text-surface-deep-fg/85">
                                <code>{CODE_SNIPPETS[lang]}</code>
                            </pre>
                        </div>

                        <div className="mt-5 flex flex-wrap items-center gap-4 text-[12.5px] text-surface-deep-fg/55">
                            <span className="inline-flex items-center gap-1.5">
                                <Clock className="w-3.5 h-3.5" /> p95 latency 72s
                            </span>
                            <span>·</span>
                            <span className="inline-flex items-center gap-1.5">
                                <Code2 className="w-3.5 h-3.5" /> Typed SDKs in 4 languages
                            </span>
                            <span>·</span>
                            <span className="inline-flex items-center gap-1.5">
                                <Globe2 className="w-3.5 h-3.5" /> Deployable in-region
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────── Pricing teaser ─────────────────────── */

function PricingTeaser() {
    return (
        <section className="relative">
            <div className="container max-w-[1240px] mx-auto px-6 py-24 md:py-28">
                <div className="rounded-3xl border border-border bg-gradient-hero p-10 md:p-14 relative overflow-hidden">
                    <div className="absolute -top-24 -right-24 w-[320px] h-[320px] bg-accent/15 blur-3xl rounded-full pointer-events-none" />
                    <div className="relative grid md:grid-cols-12 gap-10 items-center">
                        <div className="md:col-span-7">
                            <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">Pricing</p>
                            <h2 className="mt-4 font-serif text-[34px] md:text-[44px] leading-[1.05] tracking-tight text-balance">
                                Pay per statement. <span className="italic">No seat fees, no platform fees.</span>
                            </h2>
                            <p className="mt-4 text-[14.5px] text-muted-foreground max-w-[520px] leading-relaxed">
                                Starts at <span className="font-mono text-foreground">$0.28/statement</span> with volume tiers. Annual commits include in-region deployment and dedicated bank onboarding.
                            </p>
                        </div>
                        <div className="md:col-span-5 flex md:justify-end">
                            <div className="flex flex-col sm:flex-row gap-3">
                                <Button variant="hero" size="lg" asChild>
                                    <Link to="/pricing">
                                        See pricing <ArrowUpRight className="w-4 h-4 ml-1" />
                                    </Link>
                                </Button>
                                <Button variant="outline-dark" size="lg" asChild>
                                    <Link to="/contact">Talk to sales</Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────── FAQ ─────────────────────── */

const FAQS = [
    {
        q: "Do I need to train CredNX on my own statements?",
        a: "No. CredNX ships pre-trained models for every supported bank — no tuning, no templates, no manual mappings on your end.",
    },
    {
        q: "What happens when my bank isn't in the list?",
        a: "We onboard new banks in under 14 days using a small sample of real statements. It's included in annual contracts.",
    },
    {
        q: "Is CredNX deployable in-region for regulated lenders?",
        a: "Yes. We offer dedicated, in-region deployments in Indonesia, Singapore, Malaysia, Philippines and Vietnam for annual customers.",
    },
    {
        q: "How do you handle PII and data retention?",
        a: "Documents are processed ephemerally by default — nothing is written to disk beyond audit metadata. Retention is configurable per session.",
    },
    {
        q: "Can I get a decision instead of raw data?",
        a: "The Underwriting Workspace (coming soon) layers policy rules on top of BSA output to return a single decision. Early access available.",
    },
];

function FAQ() {
    const [open, setOpen] = useState(0);
    return (
        <section className="relative bg-background-soft/70 border-t border-border">
            <div className="container max-w-[1040px] mx-auto px-6 py-24 md:py-32">
                <div className="max-w-[700px] mb-12">
                    <p className="text-[12px] tracking-[0.22em] uppercase text-muted-foreground">FAQ</p>
                    <h2 className="mt-4 font-serif text-[38px] md:text-[54px] leading-[1.02] tracking-tight text-balance">
                        Questions, answered.
                    </h2>
                </div>

                <div className="border-t border-border">
                    {FAQS.map((f, i) => {
                        const isOpen = open === i;
                        return (
                            <div key={f.q} className="border-b border-border">
                                <button
                                    onClick={() => setOpen(isOpen ? -1 : i)}
                                    className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                                    data-testid={`faq-toggle-${i}`}
                                    aria-expanded={isOpen}
                                >
                                    <span className="font-serif text-[20px] md:text-[22px] tracking-tight text-foreground group-hover:text-primary transition-colors">
                                        {f.q}
                                    </span>
                                    <ChevronDown
                                        className={cn(
                                            "w-5 h-5 shrink-0 text-muted-foreground transition-transform duration-300",
                                            isOpen && "rotate-180 text-primary"
                                        )}
                                    />
                                </button>
                                <motion.div
                                    initial={false}
                                    animate={{
                                        height: isOpen ? "auto" : 0,
                                        opacity: isOpen ? 1 : 0,
                                    }}
                                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                    className="overflow-hidden"
                                >
                                    <p className="pb-6 pr-10 text-[15px] leading-relaxed text-muted-foreground max-w-[720px]">
                                        {f.a}
                                    </p>
                                </motion.div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────── Page ─────────────────────── */

export default function BSA() {
    return (
        <div data-testid="page-bsa">
            <BsaHero />
            <LiveBanks />
            <SampleReport />
            <Capabilities />
            <ApiPreview />
            <PricingTeaser />
            <FAQ />
        </div>
    );
}
